import { useState } from 'react';
import { ArrowLeft, Check, Clock, Pencil, Plus, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../redux/features/user/userSelectors';
import {
  useDeleteReminderMutation,
  useFinishReminderMutation,
} from '../../../../redux/services/reminders/remindersApi';

import { IconButtonTemplate } from '../../buttons/IconButtonTemplate';
import { ButtonTemplate } from '../../buttons/ButtonTemplate';
import { DialogConfirm } from '../../DialogConfirm';
import { LoaderProgress } from '../../LoaderProgress';
import { AddReminderForm } from '../../../form/forms/AddReminderForm';
import { RescheduleReminderForm } from '../../../form/forms/RescheduleReminderForm';

import { errorMessages } from '../../../../data/constants';
import { reminderTypeBadgeClasses, reminderTypeTogleData } from '../../../../lib/mocks/add-reminder-mock';

const viewTitles = {
  add: 'New reminder',
  edit: 'Edit reminder',
  reschedule: 'Reschedule',
  delete: 'Delete reminder',
};

export const ReminderGroupDialog = ({ head = {}, group = [] }) => {
  const [view, setView] = useState(null);

  const userToken = useSelector(selectUserToken);
  const [finishReminder, { isLoading: isFinishing }] = useFinishReminderMutation();
  const [deleteReminder, { isLoading: isDeleting }] = useDeleteReminderMutation();

  const backToList = () => setView(null);

  const handleFinish = async id => {
    const response = await finishReminder({ token: userToken, id });
    if (response?.error) toast.error(errorMessages.wentWrong);
  };

  const handleDelete = async id => {
    const response = await deleteReminder({ token: userToken, id });
    if (response?.error) toast.error(errorMessages.wentWrong);
    backToList();
  };

  if (view) {
    const { mode, reminder } = view;

    return (
      <div className="flex flex-col gap-3 w-full">
        <div className="flex items-center gap-2">
          <IconButtonTemplate
            isSmall={true}
            isRounded={true}
            icon={ArrowLeft}
            handleClick={backToList}
          />
          <span className="text-sm text-blackColor font-semibold">
            {viewTitles[mode]}
          </span>
        </div>

        {mode === 'add' && (
          <AddReminderForm
            isClientRequest={head?.client_request}
            reminder={head}
            realtyId={head?.realty?.id}
            setIsAddOpen={backToList}
          />
        )}

        {mode === 'edit' && (
          <AddReminderForm
            isEdit={true}
            isClientRequest={reminder?.client_request}
            reminder={reminder}
            setIsAddOpen={backToList}
          />
        )}

        {mode === 'reschedule' && (
          <RescheduleReminderForm
            reminderId={reminder.id}
            setIsRescheduleOpen={backToList}
          />
        )}

        {mode === 'delete' && (
          <DialogConfirm
            isLoading={isDeleting}
            handleSubmit={() => handleDelete(reminder.id)}
            handleCancel={backToList}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      {(isFinishing || isDeleting) && <LoaderProgress />}

      <div className="flex justify-between items-center gap-2 pr-8">
        <span className="text-sm text-blackColor font-semibold">
          Reminders ({group.length})
        </span>

        <ButtonTemplate
          isIconText={true}
          icon={Plus}
          text={'Add'}
          handleClick={() => setView({ mode: 'add' })}
        />
      </div>

      <ul className="flex flex-col w-full divide-y divide-gray-200">
        {group.map(reminder => {
          const reminderType = reminderTypeTogleData.find(
            item => item.title === reminder?.reminder_type?.title
          );

          return (
            <li
              key={reminder.id}
              className="flex flex-col gap-1.5 py-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs text-blackColor font-bold">
                  {reminder?.date}
                </span>

                {reminderType && (
                  <span
                    className={`block px-1 text-[10px] font-bold ${reminderTypeBadgeClasses[reminderType.title]} rounded`}
                  >
                    {reminderType.title}
                  </span>
                )}
              </div>

              <span className="text-sm text-blackColor whitespace-pre-line break-words">
                {reminder?.subject || (
                  <span className="italic text-gray-500">No subject</span>
                )}
              </span>

              <div className="flex items-center gap-3">
                <IconButtonTemplate
                  isSmall={true}
                  icon={reminder?.is_finish ? Undo2 : Check}
                  tooltipText={reminder?.is_finish ? 'Reopen' : 'Finish'}
                  handleClick={() => handleFinish(reminder.id)}
                />
                <IconButtonTemplate
                  isSmall={true}
                  icon={Pencil}
                  tooltipText={'Edit'}
                  handleClick={() => setView({ mode: 'edit', reminder })}
                />
                <IconButtonTemplate
                  isSmall={true}
                  icon={Clock}
                  tooltipText={'Reschedule'}
                  handleClick={() => setView({ mode: 'reschedule', reminder })}
                />
                <IconButtonTemplate
                  isSmall={true}
                  icon={Trash2}
                  tooltipText={'Delete'}
                  handleClick={() => setView({ mode: 'delete', reminder })}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
